import { Close as CloseIcon } from "@mui/icons-material";
import {
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { BillEstimate, ProductName } from "domain/core/types";
import { humanReadableUsagePeriod } from "lib/util";
import React, { useCallback } from "react";
import { styled } from "styled-components";

import { CurrentCharges } from "./CurrentCharges";

const SpacerSm = styled.div`
  height: 0.5rem;
`;

interface PricingAndPlanDetailsDialogProps {
  visible: boolean;
  onClose: () => void;
  billEstimate: BillEstimate;
  GSTInclusive: boolean;
  usagePeriodValue: number;
  productName: string;
}

export default function PricingAndPlanDetailsDialog(
  props: PricingAndPlanDetailsDialogProps,
) {
  const isLowUser = props.billEstimate?.usage_type === "low";

  const handleCloseModal = useCallback(() => {
    props.onClose();
  }, [props]);

  return (
    <Container>
      <Dialog
        onClose={handleCloseModal}
        aria-label="Learn more about your Fuel back home plan."
        open={props.visible}
      >
        <DialogTitle>
          <>
            <Typography variant="h3">
              Learn more about your{" "}
              {props.productName === ProductName.Z_ELECTRIC_REWARDS
                ? "Fuel pricing plan and rewards."
                : "EV pricing plan."}
            </Typography>
            {props.onClose && (
              <IconButton
                aria-label="close"
                onClick={props.onClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isLowUser ? "Low" : "Standard"} user power rates
          </Typography>
          {isLowUser ? (
            <Typography variant="caption">
              A low user typically uses less than 8000 kWh (kilowatt-hours) of
              power a year. If you think this is not you, update your
              electricity usage above to see accurate rates.
            </Typography>
          ) : (
            <Typography variant="caption">
              A standard user typically uses more than 8000 kWh (kilowatt-hours)
              of power a year. If you think this is not you, update your
              electricity usage above to see accurate rates.
            </Typography>
          )}
          <CurrentCharges
            billEstimate={props.billEstimate}
            GSTInclusive={props.GSTInclusive}
          />
          <Typography variant="body2">Price estimates</Typography>
          <Typography variant="caption">
            Price estimates are based on the current{" "}
            {humanReadableUsagePeriod(props.usagePeriodValue)} average in your
            network region (or closest network region), and use fixed and
            variable prices for your network, including meter configuration and
            load group, based on your ICP from your address.
          </Typography>
          <SpacerSm />
          {props.productName === ProductName.Z_ELECTRIC_REWARDS ? (
            <>
              <Typography variant="body2">Fuel reward estimates</Typography>
              <Typography variant="caption">
                Fuel reward estimates are calculated based on the above
                electricity price estimate and the fuel reward calculation -
                you’ll earn 5L of fuel for every $100 that you spend on
                electricity with us.
              </Typography>
              <SpacerSm />
            </>
          ) : null}

          <Typography variant="caption" component="p">
            For more information see Z's{" "}
            <Link
              variant="caption"
              display="inline"
              href={`${process.env.REACT_APP_Z_WEBSITE_URL}power/tc-fuel`}
              target="_blank"
              aria-label="For more information see our Terms and Conditions"
            >
              Terms and Conditions
            </Link>
          </Typography>
        </DialogContent>
      </Dialog>
    </Container>
  );
}
